import {
  ColumnState,
  GridApi,
  RowDataUpdatedEvent,
  RowHeightParams,
  GridOptions,
} from 'ag-grid-community'
import { BulkSheetView } from '../../../containers/BulkSheetView'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import { useCallback, useRef } from 'react'
import {
  ProfitLossItemRow,
  ProfitLossRowBase,
  ProfitLossRowBodyBase,
} from '../profitLossItems'
import Loading from '../../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../../components/dialogs/CancelConfirmDialog'
import { LedgerAccountsTree } from '../../LedgerAccounts/ledgerAccounts'
import { AgGridReactProps } from 'ag-grid-react'
import ColumnSettingPopper from '../../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { CancelConfirmState, CancelConfirmStateType } from '.'

interface OwnProps<
  TRow extends ProfitLossRowBase<TRowBody>,
  TRowBody extends ProfitLossRowBodyBase,
> {
  gridOptions: GridOptions
  gridApi?: GridApi
  loading: boolean
  data: TRow[]
  rowHeight: ROW_HEIGHT
  cancelConfirmState: CancelConfirmStateType
  setCancelConfirmState: (state: CancelConfirmStateType) => void
  onCancelConfirm: (state: CancelConfirmStateType) => void
  accountCategory: string | undefined
  masterTree: LedgerAccountsTree[]
  summaryRows: TRow[]
  createRow?: (
    financialState: LedgerAccountsTree,
    generalLedger: LedgerAccountsTree
  ) => TRow[]
  applicationFunctionUuid: string
  columnSetting: any
  columnState: ColumnState[]
  openSavedUiStateDialog: () => void
  initializeColumnState: () => void
}

type Props<
  TRow extends ProfitLossRowBase<TRowBody>,
  TRowBody extends ProfitLossRowBodyBase,
> = OwnProps<TRow, TRowBody> & Omit<AgGridReactProps, 'gridOptions'>

const ProfitLossItemsSimpleView = <
  TRow extends ProfitLossRowBase<TRowBody>,
  TRowBody extends ProfitLossRowBodyBase,
>({
  gridOptions,
  gridApi,
  loading,
  data,
  rowHeight,
  cancelConfirmState,
  setCancelConfirmState,
  onCancelConfirm,
  summaryRows,
  onColumnMoved,
  onColumnResized,
  onColumnVisible,
  onFilterChanged,
  onFirstDataRendered,
  onGridReady,
  onRowGroupOpened,
  onSortChanged,
  onCellValueChanged,
  getContextMenuItems,
  isExternalFilterPresent,
  doesExternalFilterPass,
  applicationFunctionUuid,
  columnSetting,
  columnState,
  openSavedUiStateDialog,
  initializeColumnState,
}: Props<TRow, TRowBody>) => {
  const ref = useRef<HTMLDivElement>(null)

  const onCancelConfirmCallback = useCallback(() => {
    setCancelConfirmState(CancelConfirmState.Close)
    onCancelConfirm(cancelConfirmState)
  }, [cancelConfirmState, setCancelConfirmState, onCancelConfirm])

  const onRowDataUpdated = useCallback(
    (e: RowDataUpdatedEvent<ProfitLossItemRow>) => {
      e.api?.refreshCells({ columns: ['rowNumber'], force: true })
    },
    []
  )

  const getRowHeight = useCallback(
    (params: RowHeightParams): number => {
      if (!!params.data.isTotal) return ROW_HEIGHT.SMALL
      return rowHeight
    },
    [rowHeight]
  )

  return (
    <>
      <BulkSheetView
        ref={ref}
        rowData={data}
        gridOptions={gridOptions}
        getRowHeight={getRowHeight}
        getContextMenuItems={getContextMenuItems}
        onRowDataUpdated={onRowDataUpdated}
        onColumnMoved={onColumnMoved}
        onColumnResized={onColumnResized}
        onColumnVisible={onColumnVisible}
        onFilterChanged={onFilterChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onRowGroupOpened={onRowGroupOpened}
        onSortChanged={onSortChanged}
        pinnedTopRowData={summaryRows}
        onCellValueChanged={onCellValueChanged}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
      />
      <Loading isLoading={loading} elem={ref.current} />
      <CancelConfirmDialog
        open={CancelConfirmState.Close !== cancelConfirmState}
        onConfirm={onCancelConfirmCallback}
        onClose={() => {
          setCancelConfirmState(CancelConfirmState.Close)
        }}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        gridApi={gridApi}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={openSavedUiStateDialog}
        initializeColumnState={initializeColumnState}
        applicationFunctionUuid={applicationFunctionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={columnState}
        offset={90}
      />
    </>
  )
}

export default ProfitLossItemsSimpleView
